import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useData } from '../context/DataContext';
import GenerativeCanvas from './GenerativeCanvas';

const Layout = () => {
  const { bio } = useData();
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Close menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Close menu on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    
    // Add listener to window for global scroll
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobileMenuOpen]);

  return (
    <div className="flex flex-col md:flex-row min-h-screen font-sans selection:bg-black selection:text-white relative overflow-hidden">
      
      {/* Background Interactive Animation */}
      <GenerativeCanvas />

      {/* Mobile Header / Burger Button */}
      <div className="md:hidden fixed top-0 left-0 w-full p-6 z-50 flex justify-between items-start pointer-events-none mix-blend-difference text-white">
         {/* Logo in top bar - visible when menu is CLOSED */}
        <h1 className={`text-xl font-bold tracking-tighter uppercase transition-opacity duration-300 pointer-events-auto ${isMobileMenuOpen ? 'opacity-0' : 'opacity-100'}`}>
          <NavLink to="/">{bio.name}</NavLink>
        </h1>
        
        {/* Burger / Close Toggle */}
        <button 
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 -mr-2 hover:opacity-70 transition-opacity focus:outline-none pointer-events-auto"
          aria-label="Toggle Menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Sidebar / Navigation Overlay */}
      <aside 
        className={`
          fixed inset-0 md:inset-auto md:top-0 md:left-0
          w-full md:w-64 lg:w-80 h-screen
          p-8 flex flex-col justify-between z-40
          transition-all duration-500 ease-in-out
          ${isMobileMenuOpen 
            ? 'bg-white/90 backdrop-blur-md opacity-100 pointer-events-auto translate-y-0' 
            : 'bg-transparent pointer-events-none opacity-0 -translate-y-10 md:opacity-100 md:pointer-events-auto md:translate-y-0 md:bg-transparent md:backdrop-blur-none'
          }
        `}
      >
        <div className="pt-20 md:pt-0">
          <h1 className="text-2xl font-bold tracking-tighter uppercase mb-8 md:block hidden">
            <NavLink to="/">{bio.name}</NavLink>
          </h1>
          
          {/* Mobile Menu Logo (inside overlay) */}
          <h1 className="text-3xl font-bold tracking-tighter uppercase mb-12 md:hidden text-black">
            <NavLink to="/">{bio.name}</NavLink>
          </h1>
          
          <nav 
            className="flex flex-col space-y-6 md:space-y-2 text-xl md:text-sm uppercase tracking-wide font-medium text-neutral-500 md:text-neutral-500 text-black"
            style={{ 
              // Apply text shadow only on desktop for readability over particles without bg
              // On mobile we have the bg-white/90 so we don't need heavy shadow
              textShadow: window.innerWidth >= 768 ? '0 0 12px #ffffff, 0 0 20px #ffffff, 0 0 30px #ffffff' : 'none' 
            }}
          >
            <NavLink 
              to="/films" 
              className={({ isActive }) => 
                `hover:text-black transition-colors ${isActive ? 'text-black' : ''}`
              }
            >
              1. Films
            </NavLink>
            <NavLink 
              to="/generative" 
              className={({ isActive }) => 
                `hover:text-black transition-colors ${isActive ? 'text-black' : ''}`
              }
            >
              2. Generative Art
            </NavLink>
            <NavLink 
              to="/immersive" 
              className={({ isActive }) => 
                `hover:text-black transition-colors ${isActive ? 'text-black' : ''}`
              }
            >
              3. Immersive
            </NavLink>
            <NavLink 
              to="/awards" 
              className={({ isActive }) => 
                `hover:text-black transition-colors ${isActive ? 'text-black' : ''}`
              }
            >
              4. Awards
            </NavLink>
            <NavLink 
              to="/exhibitions" 
              className={({ isActive }) => 
                `hover:text-black transition-colors ${isActive ? 'text-black' : ''}`
              }
            >
              5. Exhibition Archive
            </NavLink>
            <NavLink 
              to="/research" 
              className={({ isActive }) => 
                `hover:text-black transition-colors ${isActive ? 'text-black' : ''}`
              }
            >
              6. Research
            </NavLink>
            <NavLink 
              to="/experimental" 
              className={({ isActive }) => 
                `hover:text-black transition-colors ${isActive ? 'text-black' : ''}`
              }
            >
              7. Experimental Works
            </NavLink>
            <div className="h-4"></div>
            <NavLink 
              to="/about" 
              className={({ isActive }) => 
                `hover:text-black transition-colors ${isActive ? 'text-black' : ''}`
              }
            >
              About
            </NavLink>
            <a 
              href={`mailto:${bio.email}`} 
              className="hover:text-black transition-colors"
            >
              Contact
            </a>
          </nav>
        </div>

        <div className="text-xs text-neutral-400 mt-auto">
          <p>© {new Date().getFullYear()} LKDN</p>
        </div>
      </aside>

      {/* Main Content Area */}
      <main 
        className={`
          flex-1 md:ml-64 lg:ml-80 min-h-screen transition-all duration-700
          ${isHome ? 'bg-transparent pointer-events-none' : 'bg-white/60 backdrop-blur-md pointer-events-auto'}
        `}
      >
        <div className={`
          p-4 md:p-10 lg:p-12 xl:pr-20 w-full max-w-[1800px] mx-auto md:mx-0 pt-24 md:pt-10
          ${isHome ? 'opacity-0' : 'opacity-100'}
          transition-opacity duration-500
        `}>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
